import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  FindReplace as FindReplaceIcon,
  ToggleOff as ToggleOffIcon,
} from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IResponsibilityBrand } from '../../../../models/responsibilityBrand.model';
import { fetchResponsibilitySalesTeamMember } from '../../../../services/responsibilitySalesTeamMemberService';
import { PageFilter } from '../../../../state/app';
import { AppState } from '../../../../state/configureStore';
import {
  fetchResponsibilityBrandList,
  inactiveResponsibilityBrand,
  ResponsibilityBrandListFilter,
  ResponsibilityBrandState,
} from '../../../../state/responsibilityBrand';
import { useFilter } from '../../../hooks';
import AddResponsibilityBrandDialog from './AddResponsibilityBrandDialog';
import ResponsibilityBrandReplaceDialog from './ResponsibilityBrandReplaceDialog';
import { DateDisplay } from '../../../components';

const useStyles = makeStyles((theme) => ({
  tableRow: {
    height: '12px',
  },
  addButton: {
    position: 'absolute',
    right: '5px',
    top: '20px',
  },
  container: {
    marginTop: '20px',
    position: 'relative',
  },
  table: {
    marginTop: '30px',
    position: 'static',
  },
  showAll: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '5px',
  },
  button: {
    fontSize: 10,
  },
  title: {
    position: 'relative',
    left: '5px',
    top: '25px',
  },
}));

interface ResponsibilityBrandFormProps {
  responsibilityId?: number;
  disabled?: boolean;
}

const ResponsibilityBrandForm: React.FC<ResponsibilityBrandFormProps> = (props) => {
  const { responsibilityId, disabled = false } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [openAddResponsibilityBrandDialog, setOpenAddResponsibilityBrandDialog] =
    useState(false);
  const [openResponsibilityBrandReplaceDialog, setOpenResponsibilityBrandReplaceDialog] =
    useState(false);
  const [selectedResponsibilityBrand, setSelectedResponsibilityBrand] =
    useState<IResponsibilityBrand>({});
  const [showAll, setShowAll] = useState(false);

  const {
    items: responsibilityBrands,
    totalItems,
    loading,
  } = useSelector<AppState, ResponsibilityBrandState>(
    (state) => state.responsibilityBrand,
  );

  const [filters, setFilters] = useFilter<ResponsibilityBrandListFilter>({
    'ResponsibilityId.EqualsTo': responsibilityId,
  });
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });

  const addNewBrand = () => {
    setOpenAddResponsibilityBrandDialog(true);
  };

  const ReplaceBrand = (selectedRow: IResponsibilityBrand) => {
    setOpenResponsibilityBrandReplaceDialog(true);
    setSelectedResponsibilityBrand(selectedRow);
  };

  const handleClose = () => {
    setOpenAddResponsibilityBrandDialog(false);
  };

  const handleCloseReplace = () => {
    setOpenResponsibilityBrandReplaceDialog(false);
  };

  useEffect(() => {
    if (responsibilityId) {
      dispatch(fetchResponsibilityBrandList(filters, paging));
    }
  }, [responsibilityId]);

  return (
    <>
      <div className={classes.container}>
        <Typography className={classes.title} variant="h6" component="div">
          Brand
        </Typography>
        <div className={classes.addButton}>
          <Button
            variant="contained"
            color="primary"
            onClick={addNewBrand}
            startIcon={<AddIcon />}
            disabled={disabled}
          >
            New Brand
          </Button>
        </div>

        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>No.</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Active Date</TableCell>
              <TableCell>Inactive Date</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {responsibilityBrands
              .filter((responsibilityBrand) => {
                if (!showAll) {
                  return responsibilityBrand.active === true;
                }
                return responsibilityBrand;
              })
              .map((responsibilityBrand, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{responsibilityBrand.brand?.name}</TableCell>
                    <TableCell>
                      <DateDisplay value={responsibilityBrand.activeDate} />
                    </TableCell>
                    <TableCell>
                      <DateDisplay value={responsibilityBrand.inactiveDate} />
                    </TableCell>
                    <TableCell>
                      <Button
                        className={classes.button}
                        color="primary"
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          ReplaceBrand(responsibilityBrand!);
                        }}
                        startIcon={<FindReplaceIcon />}
                        disabled={disabled}
                      >
                        Replace
                      </Button>
                      <Button
                        className={classes.button}
                        color="secondary"
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          dispatch(inactiveResponsibilityBrand(responsibilityBrand.id!));
                        }}
                        startIcon={<ToggleOffIcon />}
                        disabled={disabled}
                      >
                        Inactive
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <div className={classes.showAll}>
          <Button
            color="primary"
            size={'small'}
            onClick={() => {
              setShowAll((previousState) => !previousState);
            }}
          >
            {showAll ? 'show less' : 'show all'}
          </Button>
        </div>
      </div>
      <AddResponsibilityBrandDialog
        open={openAddResponsibilityBrandDialog}
        handleClose={handleClose}
        responsibilityId={responsibilityId}
      />
      <ResponsibilityBrandReplaceDialog
        open={openResponsibilityBrandReplaceDialog}
        onClose={handleCloseReplace}
        responsibilityBrand={selectedResponsibilityBrand}
      />
    </>
  );
};

export default ResponsibilityBrandForm;
